import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Smartphone, 
  CheckCircle2, 
  Loader2, 
  AlertCircle, 
  LogOut, 
  QrCode, 
  RefreshCw,
  Settings
} from "lucide-react";
import { toast } from "sonner";
import { 
  checkAdminUazStatus, 
  getAdminUazQr, 
  logoutAdminUaz 
} from "@/lib/store.functions";

interface AdminWhatsAppProps {
  initialInstance: string;
  initialWhatsapp: string;
  onSave: (instance: string, whatsapp: string) => Promise<void>;
}

export function AdminWhatsApp({ initialInstance, initialWhatsapp, onSave }: AdminWhatsAppProps) {
  const [instanceName, setInstanceName] = useState(initialInstance);
  const [adminWhatsapp, setAdminWhatsapp] = useState(initialWhatsapp);
  const [status, setStatus] = useState<"disconnected" | "connecting" | "connected">("disconnected");
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const checkStatus = async (name: string) => {
    if (!name) return;
    try {
      const res = await checkAdminUazStatus(name);
      if (res.connected) {
        setStatus("connected");
        setQrCode(null);
      } else {
        setStatus("disconnected");
      }
    } catch (err) {
      console.error("Status check failed", err);
    }
  };

  useEffect(() => {
    if (initialInstance) {
      checkStatus(initialInstance);
    }
  }, [initialInstance]);

  const handleRefreshQr = async () => {
    if (!instanceName) {
      toast.error("Preencha o Nome da Instância primeiro");
      return;
    }
    
    setIsRefreshing(true);
    setQrCode(null);
    
    try {
      const res = await getAdminUazQr(instanceName);
      if (res.status === 'connected') {
        setStatus("connected");
        toast.success("Já está conectado!");
      } else if (res.qrCode) {
        setQrCode(res.qrCode);
        setStatus("connecting");
        toast.success("QR Code gerado!");
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Erro ao gerar QR Code");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = async () => {
    setIsSaving(true);
    try {
      await logoutAdminUaz(instanceName);
      setStatus("disconnected");
      setQrCode(null);
      toast.success("WhatsApp desconectado!");
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Erro ao desconectar");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await onSave(instanceName, adminWhatsapp);
      checkStatus(instanceName);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="border-none shadow-lg max-w-xl mx-auto overflow-hidden">
      <div className="bg-gradient-to-r from-[#25D366] to-[#128C7E] p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Smartphone className="h-6 w-6" />
              WhatsApp
            </h2>
            <p className="text-green-50 opacity-90 text-sm">Integração via Uazapi</p>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 backdrop-blur-md ${
            status === "connected" ? "bg-white text-green-600" : 
            status === "connecting" ? "bg-yellow-400 text-yellow-900" : 
            "bg-black/20 text-white"
          }`}>
            {status === "connected" ? (
              <><CheckCircle2 className="h-3 w-3" /> Conectado</>
            ) : status === "connecting" ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Conectando...</>
            ) : (
              <><AlertCircle className="h-3 w-3" /> Desconectado</>
            )}
          </div>
        </div>
      </div>

      <CardContent className="p-6 space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="uaz-instance" className="text-sm font-semibold text-gray-700">Nome da Instância</Label>
            <div className="flex gap-2">
              <Input 
                id="uaz-instance"
                placeholder="Ex: Gaby_Store" 
                value={instanceName}
                onChange={(e) => setInstanceName(e.target.value)}
                className="border-gray-200 focus:ring-green-500"
                disabled={status === "connected" || isSaving}
              />
              {status !== "connected" && (
                <Button 
                  variant="secondary"
                  onClick={handleSaveSettings}
                  disabled={isSaving}
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
                </Button>
              )}
            </div>
            <p className="text-[10px] text-gray-500 italic">
              Configure um nome único para sua conexão.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-wa" className="text-sm font-semibold text-gray-700">Seu WhatsApp (para receber pedidos)</Label>
            <Input 
              id="admin-wa"
              placeholder="Ex: 5511999999999 (com DDD e DDI)" 
              value={adminWhatsapp}
              onChange={(e) => setAdminWhatsapp(e.target.value)}
              className="border-gray-200 focus:ring-green-500"
              disabled={isSaving}
            />
            <p className="text-[10px] text-gray-500 italic">
              Número que receberá a notificação de cada nova encomenda.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {status === "connected" ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 bg-green-50 rounded-2xl border border-green-100 text-center space-y-4">
              <div className="bg-white p-4 rounded-full shadow-md text-green-500">
                <CheckCircle2 className="h-12 w-12" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-green-900">WhatsApp Conectado!</h3>
                <p className="text-sm text-green-700 max-w-[280px]">
                  Seu sistema agora está pronto para enviar notificações automáticas de novos pedidos.
                </p>
              </div>
              <Button 
                variant="outline" 
                className="mt-4 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-2"
                onClick={handleLogout}
                disabled={isSaving}
              >
                <LogOut className="h-4 w-4" />
                Desconectar WhatsApp
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-100 rounded-2xl p-8 bg-gray-50/50 transition-all">
              {qrCode ? (
                <div className="text-center space-y-6 animate-in fade-in zoom-in duration-300">
                  <div className="bg-white p-6 rounded-2xl shadow-xl border-4 border-green-50 inline-block relative">
                    <img src={qrCode} alt="WhatsApp QR Code" className="w-56 h-56" />
                    {status === "connecting" && (
                      <div className="absolute inset-0 bg-white/10 backdrop-blur-[1px] flex items-center justify-center rounded-xl pointer-events-none">
                        <div className="bg-white/90 p-2 rounded-full shadow-lg">
                          <Loader2 className="h-6 w-6 text-green-500 animate-spin" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-gray-800">Escaneie o QR Code</p>
                    <p className="text-xs text-gray-500 leading-relaxed max-w-[240px] mx-auto">
                      1. Abra o WhatsApp no seu celular<br />
                      2. Toque em <b>Aparelhos Conectados</b><br />
                      3. Toque em <b>Conectar um Aparelho</b>
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-4 py-4">
                  <div className="bg-white p-6 rounded-full shadow-sm border border-gray-100 inline-block text-gray-300">
                    <QrCode className="h-16 w-16" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-sm font-bold text-gray-700">Conectar Novo Aparelho</h3>
                    <p className="text-xs text-gray-500 max-w-[240px] mx-auto">
                      Clique no botão abaixo para gerar um QR Code e vincular seu número.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {status !== "connected" && (
            <Button 
              className={`w-full h-12 text-white font-bold gap-2 rounded-xl transition-all ${
                qrCode ? 'bg-gray-800 hover:bg-gray-900' : 'bg-green-600 hover:bg-green-700 shadow-lg shadow-green-100'
              }`}
              onClick={handleRefreshQr}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <RefreshCw className="h-5 w-5" />
              )}
              {qrCode ? "Gerar Novo QR Code" : "Conectar WhatsApp"}
            </Button>
          )}

          <div className="bg-blue-50 rounded-xl p-4 flex gap-4 border border-blue-100">
            <div className="bg-blue-100 p-2 rounded-lg h-fit">
              <Settings className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-blue-900">Notificações Automáticas</h4>
              <p className="text-xs text-blue-800 leading-relaxed mt-1">
                Uma vez conectado, você receberá detalhes de cada novo pedido instantaneamente no seu WhatsApp.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
